/** @jsxImportSource @emotion/react */
import { css, Theme, useTheme } from '@emotion/react'

interface Props {
  currentPage: number
  lastPage: number
  onPageChange: (page: number) => void
}

const generatePageNumbers = (
  currentPage: number,
  lastPage: number,
  delta = 2,
): (number | 'ellipsis')[] => {
  const range: (number | 'ellipsis')[] = []
  let left = currentPage - delta
  let right = currentPage + delta

  let l: number | null = null
  for (let i = 1; i <= lastPage; i++) {
    if (i === 1 || i === lastPage || (i >= left && i <= right)) {
      if (l !== null && i - l > 1) {
        range.push('ellipsis')
      }
      range.push(i)
      l = i
    }
  }

  return range
}

const containerStyle = css`
  display: flex;
  gap: 8px;
  justify-content: center;
`

const buttonBaseStyle = (t: Theme) => css`
  border: 1px solid #cccccc;
  width: 38px;
  height: 38px;
  border-radius: 4px;
  cursor: pointer;
  background-color: ${t.c.ec.white};
  color: ${t.c.ec.black};
  box-sizing: border-box;
  font-family: 'Open Sans', sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;

  &:hover {
    background-color: #f0f0f0;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const iconStyle = css`
  width: 16px;
  height: 16px;
  fill: currentColor;
`

const activeButtonStyle = (t: Theme) => css`
  background-color: #EBEBEB;
  color: ${t.c.ec.black};
  font-weight: 400;
  cursor: default;
`

export const BasePagination = ({
  currentPage,
  lastPage,
  onPageChange,
}: Props) => {
  const theme = useTheme()
  const pages = generatePageNumbers(currentPage, lastPage)

  return (
    <div css={containerStyle}>
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        css={buttonBaseStyle(theme)}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 320 512"
          css={iconStyle}
        >
          <path d="M9.4 233.4c-12.5 12.5-12.5 32.8 0 45.3l192 192c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L77.3 256 246.6 86.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0l-192 192z" />
        </svg>
      </button>

      {pages.map((page, index) =>
        page === 'ellipsis' ? (
          <span key={`ellipsis-${index}`} css={{ padding: '4px 6px' }}>
            …
          </span>
        ) : (
          <button
            key={page}
            onClick={() => onPageChange(page)}
            disabled={page === currentPage}
            css={[
              buttonBaseStyle(theme),
              page === currentPage && activeButtonStyle(theme),
            ]}
          >
            {page}
          </button>
        ),
      )}

      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === lastPage}
        css={buttonBaseStyle(theme)}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 320 512"
          css={iconStyle}
        >
          <path d="M310.6 233.4c12.5 12.5 12.5 32.8 0 45.3l-192 192c-12.5 12.5-32.8 12.5-45.3 0s-12.5-32.8 0-45.3L242.7 256 73.4 86.6c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0l192 192z" />
        </svg>
      </button>
    </div>
  )
}
